import { useEffect, useState, useCallback } from 'react'
import { api } from '../../lib/api'

interface DeviceStatus {
  id: string
  site_device_id: string | null
  discovered_ip: string
  discovered_mac: string
  discovered_model: string
  discovered_serial: string
  discovered_firmware: string
  discovery_method: string
  status: string
  device_label: string | null
  hostname: string | null
  expected_ip: string | null
}

interface JobDetail {
  id: string
  status: string
  total_devices: number
  discovered_devices: string | null
  matched_devices: string | null
  profile_name: string
  devices: DeviceStatus[]
}

interface DiscoveryResultsProps {
  jobId: string
  onClose: () => void
}

export function DiscoveryResults({ jobId, onClose }: DiscoveryResultsProps) {
  const [job, setJob] = useState<JobDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState('')

  const loadJob = useCallback(async () => {
    try {
      const res = await api.get<JobDetail>(`/provisioning-jobs/${jobId}/status`)
      setJob(res)
    } catch { setError('Failed to load job') }
    finally { setLoading(false) }
  }, [jobId])

  useEffect(() => { loadJob() }, [loadJob])

  async function handleStartProvisioning() {
    setStarting(true)
    try {
      await api.post(`/provisioning-jobs/${jobId}/start`)
      onClose()
    } catch { setError('Failed to start provisioning') }
    finally { setStarting(false) }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>Loading...</div>
  if (!job) return <div style={{ padding: 40, color: 'var(--text-secondary)' }}>Job not found.</div>

  const matched = job.devices?.filter(d => d.site_device_id) || []
  const unmatched = job.devices?.filter(d => !d.site_device_id) || []

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 14, fontWeight: 500 }}
          >
            &larr; Back
          </button>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
            Discovery Results
          </h2>
        </div>
        <button
          onClick={loadJob}
          style={{
            padding: '5px 12px', fontSize: 12, fontWeight: 500,
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 6, cursor: 'pointer', color: 'var(--text-secondary)',
          }}
        >
          Re-Discover
        </button>
      </div>

      {error && (
        <div style={{
          padding: '8px 12px', fontSize: 13, color: 'var(--err)',
          background: 'rgba(229,62,62,0.06)', borderRadius: 6, marginBottom: 12,
        }}>
          {error}
        </div>
      )}

      {/* Summary */}
      <div style={{
        padding: '12px 16px', marginBottom: 16,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', fontSize: 13, color: 'var(--text-secondary)',
      }}>
        Found {job.devices?.length || 0} devices on the network
        {matched.length > 0 && <> &middot; <span style={{ color: 'var(--ok)' }}>{matched.length} matched</span></>}
        {unmatched.length > 0 && <> &middot; <span style={{ color: 'var(--warn)' }}>{unmatched.length} unmatched</span></>}
      </div>

      {/* Device List */}
      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          padding: '8px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg)',
        }}>
          <span>Discovered</span>
          <span>Matched To</span>
        </div>

        {job.devices?.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
            No devices discovered yet. The Site Agent will populate this when it runs discovery.
          </div>
        ) : (
          job.devices?.map(device => (
            <div key={device.id} style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              padding: '12px 16px',
              borderBottom: '1px solid var(--border-light, var(--border))',
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
                  {device.discovered_ip || 'Unknown IP'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  {device.discovered_model || 'Unknown model'}
                  {device.discovered_mac && <> · MAC: <span style={{ fontFamily: 'var(--font-mono)' }}>{device.discovered_mac}</span></>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>
                  via {device.discovery_method || 'scan'}
                  {device.site_device_id && (
                    <span style={{ color: 'var(--ok)', marginLeft: 8 }}>Auto-matched</span>
                  )}
                  {!device.site_device_id && (
                    <span style={{ color: 'var(--warn)', marginLeft: 8 }}>Manual match needed</span>
                  )}
                </div>
              </div>
              <div>
                {device.site_device_id ? (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                      {device.device_label || device.hostname || 'Unnamed device'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                      {device.expected_ip && `Expected IP: ${device.expected_ip}`}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    No match — assign manually
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button
          onClick={onClose}
          style={{
            padding: '7px 14px', fontSize: 13, fontWeight: 500,
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-secondary)',
          }}
        >
          Confirm Matches
        </button>
        <button
          onClick={handleStartProvisioning}
          disabled={starting}
          style={{
            padding: '7px 14px', fontSize: 13, fontWeight: 600,
            background: 'var(--primary)', color: '#fff', border: 'none',
            borderRadius: 'var(--radius)', cursor: 'pointer',
            opacity: starting ? 0.6 : 1,
          }}
        >
          {starting ? 'Starting...' : 'Start Provisioning'}
        </button>
      </div>
    </div>
  )
}
